import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/patterns/empty-state";
import { reportError } from "@/app/observability";

/**
 * THE LAST THING BETWEEN A RENDER CRASH AND A WHITE SCREEN.
 *
 * On the web a crashed app is a reload away and the console says why. Inside an app there
 * is no console, no reload button and no network tab — a thrown error in a component is a
 * white screen and a one-star review. So the boundary does two things: it reports (nobody
 * else will), and it offers the one action that actually helps.
 *
 * Written by hand rather than taken from the reporting SDK, so it works identically when no
 * DSN is configured — which is every project's first week.
 */
interface Props {
  children: ReactNode;
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  crashed: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, { componentStack: info.componentStack });
  }

  reset = () => this.setState({ crashed: false });

  render() {
    if (!this.state.crashed) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);

    /**
     * ⚠️ A DECISION: the design system leaves the screen-level error state undecided
     * (13.3), so this is assembled from its empty-state pattern and its button rather than
     * styled from scratch.
     */
    return (
      <div className="pt-safe pb-safe flex min-h-dvh flex-col justify-center">
        <EmptyState
          title="Something broke"
          description="It has been reported. Try again — if it keeps happening, closing and reopening the app usually clears it."
          action={<Button onClick={this.reset}>Try again</Button>}
        />
      </div>
    );
  }
}
